import React, { useEffect, useState } from 'react';
import {
    Table,
    Row,
    Col,
    Button,
    Modal,
    PaginationProps,
} from 'antd';
import { getRequest, deleteRequest, ResponseDataType, postRequest } from 'api';
import { toast } from 'react-toastify';
import { ExclamationCircleFilled, PlusOutlined, RightOutlined, DownOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { CreateClassYear } from '../Create';
import { ExpandedStudentList } from './ExpandedStudentList';
import { ClassYearInfo } from '../Information';
import { PromotionModal } from '../Promotion';
import { ClassLevelSelect, DatePicker } from 'Components';

const { confirm } = Modal;

interface ClassYearDataType {
    classYearId: number;
    classId: number;
    classLevel: string;
    className: string;
    schoolId: number;
    studentCount?: number;
    status?: number;
}

export const ClassYearList = () => {
    const [selectedYear, setSelectedYear] = useState<dayjs.Dayjs | null>(dayjs());
    const [startYear, setStartYear] = useState<number | null>(null);
    const [classYears, setClassYears] = useState<ClassYearDataType[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [sort, setSort] = useState<string>('asc');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);
    const [selectedClassYearId, setSelectedClassYearId] = useState<number | undefined>();
    const [loading, setLoading] = useState(false);
    const [showPromotionModal, setShowPromotionModal] = useState(false);
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
    const [selectedClassLevel, setSelectedClassLevel] = useState<string | undefined>(undefined);

    const sortBy = 'className';

    const schoolIdStr = localStorage.getItem('schoolId');
    const schoolId = schoolIdStr ? parseInt(schoolIdStr, 10) : null;

    const fetchClassYears = async (year: number, classLevel?: string) => {
        if (!schoolId || isNaN(schoolId)) {
            toast.error('Không tìm thấy schoolId – vui lòng đăng nhập lại');
            return false;
        }

        const queryParams = new URLSearchParams({
            start: year.toString(),
            schoolId: schoolId.toString(),
            page: currentPage.toString(),
            perPage: pageSize.toString(),
            sort,
            sortBy,
        });

        const level = classLevel !== undefined ? classLevel : selectedClassLevel;
        if (level) {
            queryParams.append('classLevel', level);
        }

        const url = `/cms/class/get-class-years-by-start-end-schoolId?${queryParams.toString()}`;

        setLoading(true);
        try {
            const response: ResponseDataType<{
                list: ClassYearDataType[];
                total: number;
            }> = await getRequest(url);

            if (response?.code === 200) {
                const hasData = (response.info?.list?.length || 0) > 0;
                if (hasData) {
                    setStartYear(year);
                    setSelectedYear(dayjs().year(year));
                    setClassYears(response.info?.list || []);
                    setTotalItems(response.info?.total || 0);
                } else {
                    setClassYears([]);
                    setTotalItems(0);
                }
                return hasData;
            } else {
                toast.error(response?.msg || 'Lỗi khi tải danh sách lớp');
            }
        } catch (error) {
            toast.error('Không thể kết nối đến máy chủ');
        } finally {
            setLoading(false);
        }

        return false;
    };

    useEffect(() => {
        const loadInitialData = async () => {
            const now = dayjs();
            const defaultYear = now.month() < 7 ? now.subtract(1, 'year').year() : now.year();
            await fetchClassYears(defaultYear);
        };
        loadInitialData();
    }, []);

    useEffect(() => {
        if (startYear !== null && !isNaN(startYear)) {
            fetchClassYears(startYear);
        }
    }, [currentPage, pageSize, sort]);

    const handleSearch = async (overrideLevel?: string) => {
        if (!selectedYear) {
            toast.warn('Vui lòng chọn năm');
            return;
        }
        const year = selectedYear.year();
        setCurrentPage(1);
        const level = overrideLevel !== undefined ? overrideLevel : selectedClassLevel;
        await fetchClassYears(year, level);
    };

    const handlePageChange: PaginationProps['onChange'] = (page, size) => {
        setCurrentPage(page);
        setPageSize(size || 10);
    };
    const handleEndClassYears = async () => {
        if (!classYears || classYears.length === 0) {
            toast.warn('Không có lớp nào để kết thúc năm học');
            return;
        }

        confirm({
            title: 'Bạn có chắc chắn muốn kết thúc năm học của tất cả các lớp?',
            icon: <ExclamationCircleFilled />,
            // content: 'Hành động này sẽ thay đổi trạng thái học sinh trong lớp.',
            okText: 'Xác nhận',
            cancelText: 'Hủy',
            async onOk() {
                try {
                    // Lấy tất cả classYearId từ danh sách
                    const queryParams = classYears
                        .map(c => `classYearIds=${c.classYearId}`)
                        .join('&');

                    const url = `/cms/class/end-class-year?${queryParams}`;

                    const res = await postRequest(url, {});

                    if (res?.code === 200) {
                        toast.success('Kết thúc năm học thành công');
                    } else {
                        toast.error(res?.msg || 'Kết thúc thất bại');
                    }

                    if (startYear) fetchClassYears(startYear);
                } catch (error) {
                    toast.error('Có lỗi khi kết thúc năm học');
                }
            },
        });
    };

    const handleEndSingleClassYear = (classYearId: number) => {
        confirm({
            title: 'Bạn có chắc chắn muốn kết thúc năm học của lớp này?',
            icon: <ExclamationCircleFilled />,
            // content: 'Hành động này sẽ thay đổi trạng thái học sinh trong lớp.',
            okText: 'Xác nhận',
            cancelText: 'Hủy',
            async onOk() {
                try {
                    const url = `/cms/class/end-class-year?classYearIds=${classYearId}`;
                    const res = await postRequest(url, {}); // gọi POST không cần body

                    if (res?.code === 200) {
                        toast.success('Kết thúc lớp học thành công');
                        if (startYear) fetchClassYears(startYear);
                    } else {
                        toast.error(res?.msg || 'Kết thúc thất bại');
                    }
                } catch (error) {
                    toast.error('Có lỗi khi kết thúc lớp học');
                }
            },
        });
    };

    const handleReopenSingleClassYear = (classYearId: number) => {
        confirm({
            title: 'Bạn có chắc chắn muốn mở lại lớp học này?',
            icon: <ExclamationCircleFilled />,
            okText: 'Xác nhận',
            cancelText: 'Hủy',
            async onOk() {
                try {
                    const url = `/cms/class/reopen-class-year?classYearIds=${classYearId}`;
                    const res = await postRequest(url, {});

                    if (res?.code === 200) {
                        toast.success('Mở lại lớp học thành công');
                        if (startYear) fetchClassYears(startYear);
                    } else {
                        toast.error(res?.msg || 'Mở lại thất bại');
                    }
                } catch (error) {
                    toast.error('Có lỗi khi mở lại lớp học');
                }
            },
        });
    };

    const handleReopenClassYears = async () => {
        const closedClasses = classYears.filter(c => c.status === 0);
        if (!closedClasses || closedClasses.length === 0) {
            toast.warn('Không có lớp nào đã kết thúc để mở lại');
            return;
        }

        confirm({
            title: 'Bạn có chắc chắn muốn mở lại tất cả các lớp đã kết thúc trong năm học này?',
            icon: <ExclamationCircleFilled />,
            okText: 'Xác nhận',
            cancelText: 'Hủy',
            async onOk() {
                try {
                    const queryParams = closedClasses
                        .map(c => `classYearIds=${c.classYearId}`)
                        .join('&');

                    const url = `/cms/class/reopen-class-year?${queryParams}`;
                    const res = await postRequest(url, {});

                    if (res?.code === 200) {
                        toast.success('Mở lại năm học thành công');
                    } else {
                        toast.error(res?.msg || 'Mở lại thất bại');
                    }

                    if (startYear) fetchClassYears(startYear);
                } catch (error) {
                    toast.error('Có lỗi khi mở lại năm học');
                }
            },
        });
    };

    const showDeleteConfirm = (classYearId: number) => {
        confirm({
            title: 'Bạn có chắc chắn muốn xóa lớp này?',
            icon: <ExclamationCircleFilled />,
            content: 'Hành động này không thể hoàn tác.',
            okText: 'Xác nhận',
            okType: 'danger',
            cancelText: 'Hủy',
            async onOk() {
                const url = `/cms/class/delete-class-year/${classYearId}`;
                const response: ResponseDataType<{}> = await deleteRequest(url);
                if (response?.code === 200) {
                    toast.success('Xóa lớp thành công');
                    if (startYear) fetchClassYears(startYear);
                } else {
                    toast.error(response?.msg || 'Xóa thất bại');
                }
            },
        });
    };

    const columns: ColumnsType<ClassYearDataType> = [
        {
            title: 'Tên lớp',
            dataIndex: 'className',
            key: 'className',
            align: 'center',
            sorter: true,
            sortDirections: ['ascend', 'descend', 'ascend'],
            sortOrder: sort === 'asc' ? 'ascend' : 'descend',
        },
        { title: 'Khối lớp', dataIndex: 'classLevel', key: 'classLevel', align: 'center' },
        { title: 'Sĩ số', dataIndex: 'studentCount', key: 'studentCount', align: 'center' },
        { title: 'Năm học', dataIndex: 'schoolYear', key: 'schoolYear', align: 'center' },
        { title: 'Mô tả', dataIndex: 'description', key: 'description', align: 'center' },
        { 
            title: 'Trạng thái', 
            dataIndex: 'status', 
            key: 'status', 
            align: 'center',
            render: (status) => (
                <span style={{ color: status === 0 ? '#cf1322' : '#389e0d', fontWeight: 500 }}>
                    {status === 0 ? 'Đã kết thúc' : 'Đang học'}
                </span>
            )
        },

        {
            title: 'Hành động',
            key: 'action',
            dataIndex: 'classYearId',
            align: 'center',
            render: (classYearId: number, record: ClassYearDataType) => (
                <>
                    {record.status === 0 ? (
                        <span
                            style={{ color: '#52c41a', marginLeft: 10, cursor: 'pointer' }}
                            onClick={() => handleReopenSingleClassYear(classYearId)}
                        >
                            Mở lại lớp học
                        </span>
                    ) : (
                        <span
                            style={{ color: 'orange', marginLeft: 10, cursor: 'pointer' }}
                            onClick={() => handleEndSingleClassYear(classYearId)}
                        >
                            Kết thúc lớp học
                        </span>
                    )}
                    <span
                        style={{ color: 'blue', marginLeft: 10, cursor: 'pointer' }}
                        onClick={() => {
                            setSelectedClassYearId(classYearId);
                            setIsDetailModalOpen(true);
                        }}
                    >
                        Chi tiết
                    </span>

                    <span
                        style={{ color: 'red', marginLeft: 10, cursor: 'pointer' }}
                        onClick={() => showDeleteConfirm(classYearId)}
                    >
                        Xóa
                    </span>

                </>
            ),
        },
    ];

    const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
        if (!Array.isArray(sorter) && sorter.field === 'className') {
            const newSort = sorter.order === 'descend' ? 'desc' : 'asc';
            if (newSort !== sort) {
                setSort(newSort);
            }
        }
    };

    return (
        <div>
            <Row gutter={8} style={{ marginBottom: 16, justifyContent: 'space-between' }}>
                <Col>
                    <DatePicker
                        picker="year"
                        placeholder="Chọn năm học"
                        format={(val) => `${val.year()}-${val.year() + 1}`}
                        value={selectedYear}
                        onChange={(date) => setSelectedYear(date)}
                        style={{ width: 200, marginRight: 8 }}
                    />
                    <ClassLevelSelect
                        value={selectedClassLevel}
                        onChange={(value) => {
                            setSelectedClassLevel(value);
                            if (selectedYear) {
                                setCurrentPage(1);
                                fetchClassYears(selectedYear.year(), value);
                            }
                        }}
                        placeholder="Chọn khối"
                        style={{ width: 140, marginRight: 8 }}
                    />
                    <Button type="primary" onClick={() => handleSearch()} style={{ marginRight: 8 }}>
                        Tìm kiếm
                    </Button>
                </Col>


                <Col>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setShowCreateModal(true)}
                        style={{ marginRight: 8 }}
                    >
                        Thêm năm học
                    </Button>

                    <Button
                        // danger
                        type="primary"
                        onClick={handleEndClassYears}
                        style={{ marginRight: 8 }}
                    >
                        Kết thúc năm học
                    </Button>

                    <Button
                        type="primary"
                        onClick={handleReopenClassYears}
                        style={{ marginRight: 8 }}
                    >
                        Mở lại năm học
                    </Button>

                    <Button
                        type="primary"
                        onClick={() => setShowPromotionModal(true)}
                    >
                        Lên lớp đồng loạt
                    </Button>
                </Col>
            </Row>

            <Table
                onChange={handleTableChange}
                loading={loading}
                columns={columns}
                dataSource={classYears}
                rowKey="classYearId"
                expandable={{
                    expandedRowRender: (record) => (
                        <ExpandedStudentList
                            classYearId={record.classYearId}
                            classTitle={record.className}
                            onRefreshParent={() => {
                                if (startYear) fetchClassYears(startYear);
                            }}
                        />
                    ),
                    rowExpandable: (record) => (record.studentCount || 0) > 0,
                    expandedRowKeys: expandedRowKeys,
                    onExpand: (expanded, record) => {
                        setExpandedRowKeys(expanded ? [record.classYearId] : []);
                    },
                    expandIcon: ({ expanded, onExpand, record }) => {
                        if ((record.studentCount || 0) === 0) {
                            return <span style={{ display: 'inline-block', width: 14 }} />;
                        }
                        return expanded ? (
                            <DownOutlined
                                onClick={e => onExpand(record, e)}
                                style={{ color: '#B9392E', cursor: 'pointer', fontSize: '11px', transition: 'all 0.2s' }}
                            />
                        ) : (
                            <RightOutlined
                                onClick={e => onExpand(record, e)}
                                style={{ color: '#666', cursor: 'pointer', fontSize: '11px', transition: 'all 0.2s' }}
                            />
                        );
                    }
                }}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalItems,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50'],
                    onChange: handlePageChange,
                }}
            />

            <Modal
                title="Tạo năm học mới"
                open={showCreateModal}
                onCancel={() => setShowCreateModal(false)}
                footer={null}
                destroyOnClose
                width={800}
            >
                <CreateClassYear
                    onSuccess={() => {
                        if (startYear) fetchClassYears(startYear);
                        setShowCreateModal(false);
                    }}
                />
            </Modal>
            
            <Modal
                title="Chi tiết lớp học theo năm"
                open={isDetailModalOpen}
                onCancel={() => {
                    setIsDetailModalOpen(false);
                    setSelectedClassYearId(undefined);
                }}
                footer={null}
                width={1250}
                destroyOnClose
            >
                <div style={{ maxHeight: '75vh', overflowY: 'auto', paddingRight: '8px' }}>
                    {selectedClassYearId && <ClassYearInfo classYearId={selectedClassYearId} />}
                </div>
            </Modal>
            
            {showPromotionModal && schoolId && (
                <PromotionModal
                    open={showPromotionModal}
                    onClose={() => setShowPromotionModal(false)}
                    onSuccess={() => {
                        setShowPromotionModal(false);
                        if (startYear) fetchClassYears(startYear);
                    }}
                    schoolId={schoolId}
                    initialYear={startYear || dayjs().year()}
                />
            )}
        </div>
    );
};
